import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import store from '../../reducers/storeRegistration';
import appHistory from '../../utils/appHistory';
import { resolveDefaultLandingPageUrl } from '../../utils/userDefaultLanding';
import { readUserPreferencesContentForDefaultLanding } from '../favorites/userPreferencesFavorites';
import { finishLandingCheck, getLandingGateSnapshot, resetLandingGate, startLandingCheck } from './postAuthLandingGate';
const sleep = ms => new Promise(r => {
  setTimeout(r, ms);
});
const SAVED_VIEW_POLL_MS = 80;
const SAVED_VIEW_POLL_COUNT = 8;
const DEFAULT_LANDING_CONSUMED_KEY_PREFIX = 'postAuthDefaultLandingConsumed:';
const getDefaultLandingConsumedKey = uid => "".concat(DEFAULT_LANDING_CONSUMED_KEY_PREFIX).concat(uid);

/** Persist that the one-time post-auth default landing redirect has run for this tab session. */
export const markDefaultLandingConsumed = uid => {
  if (typeof sessionStorage === 'undefined' || !uid) {
    return;
  }
  try {
    sessionStorage.setItem(getDefaultLandingConsumedKey(uid), '1');
  } catch (_unused) {
    // SessionStorage may be disabled (e.g. private browsing)
  }
};
export const hasDefaultLandingBeenConsumed = uid => {
  if (typeof sessionStorage === 'undefined' || !uid) {
    return false;
  }
  try {
    return sessionStorage.getItem(getDefaultLandingConsumedKey(uid)) === '1';
  } catch (_unused2) {
    return false;
  }
};
const getSavedViewsFromStore = () => {
  var _store$getState$user;
  return ((_store$getState$user = store.getState().user) === null || _store$getState$user === void 0 || (_store$getState$user = _store$getState$user.active) === null || _store$getState$user === void 0 || (_store$getState$user = _store$getState$user.content) === null || _store$getState$user === void 0 ? void 0 : _store$getState$user.savedViews) || {};
};

/**
 * Loads userPreferences and returns a default landing path, or null.
 * Uses Redux for savedViews so it does not depend on React context.
 */
export const resolveDefaultLandingUrlAfterAuth = async () => {
  var _preferencesContent2;
  let preferencesContent;
  try {
    preferencesContent = await readUserPreferencesContentForDefaultLanding();
  } catch (_unused3) {
    return null;
  }
  const tryResolve = () => {
    var _preferencesContent;
    return resolveDefaultLandingPageUrl({
      defaultLandingPage: (_preferencesContent = preferencesContent) === null || _preferencesContent === void 0 ? void 0 : _preferencesContent.defaultLandingPage,
      preferencesContent,
      savedViews: getSavedViewsFromStore()
    });
  };
  let target = tryResolve();
  const dp = (_preferencesContent2 = preferencesContent) === null || _preferencesContent2 === void 0 ? void 0 : _preferencesContent2.defaultLandingPage;
  const wantsSavedView = (dp === null || dp === void 0 ? void 0 : dp.kind) === 'savedView' && typeof (dp === null || dp === void 0 ? void 0 : dp.key) === 'string' && dp.key.trim().length > 0;
  if (!target && wantsSavedView) {
    /* eslint-disable no-await-in-loop -- short poll for User.savedViews after login */
    for (let i = 0; i < SAVED_VIEW_POLL_COUNT; i += 1) {
      await sleep(SAVED_VIEW_POLL_MS);
      target = tryResolve();
      if (target) break;
    }
    /* eslint-enable no-await-in-loop */
  }
  return target && typeof target === 'string' ? target : null;
};

/**
 * After login, optionally navigate from `/` or `/file-cabinet` to userPreferences default.
 */
const PostAuthDefaultLanding = () => {
  var _login$user2;
  const login = useSelector(s => s.login);
  // Depend on the router pathname so this effect re-runs on client-side navigation
  // (e.g. clicking File Cabinet), not only on auth/user changes.
  const _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  const completedForUserRef = useRef(null);
  const inFlightRef = useRef(false);

  // Only mounted while authenticated; reset gate on unmount (logout), not via isAuthenticated.
  useEffect(() => () => {
    completedForUserRef.current = null;
    resetLandingGate();
  }, []);
  useLayoutEffect(() => {
    var _login$user;
    // Releasing the gate is mandatory whenever we will not run (and are not already
    // running) a default-landing resolution. LoginFormContainer opens the gate when a
    // landing route (`/` or `/file-cabinet`) is reached; if it is never closed the whole
    // app is hidden behind `deferMainContent` (white screen) — see CU-86e1xxher.
    const releaseGate = () => {
      if (getLandingGateSnapshot() !== 'done') {
        finishLandingCheck();
      }
    };
    if (!login.isAuthenticated) {
      return undefined;
    }
    const uid = (_login$user = login.user) === null || _login$user === void 0 ? void 0 : _login$user.user_id;
    if (!uid) {
      releaseGate();
      return undefined;
    }
    // A resolution started by a previous run owns the gate and will finish it itself.
    if (inFlightRef.current) {
      return undefined;
    }
    const isPostAuthLandingRoute = pathname === '/' || pathname === '/file-cabinet';

    // The default-landing redirect runs once per authenticated browser-tab session, for
    // the first landing route reached right after authenticating. If it already ran, or
    // we authenticated onto a non-landing route (e.g. a refresh deep in the app), the
    // opportunity is consumed: release the gate so the current route renders, and mark
    // the session resolved so a later, deliberate visit to /file-cabinet is shown as the
    // File Cabinet rather than being hijacked into the favorites-default redirect. Tab
    // sessionStorage survives refresh so /file-cabinet refresh does not re-run the
    // redirect (see chasetru PR #331 feedback). Route-agnostic for customers without FC.
    const landingOpportunityConsumed = completedForUserRef.current === uid || hasDefaultLandingBeenConsumed(uid);
    if (landingOpportunityConsumed || !isPostAuthLandingRoute) {
      completedForUserRef.current = uid;
      markDefaultLandingConsumed(uid);
      releaseGate();
      return undefined;
    }
    inFlightRef.current = true;
    let cancelled = false;
    if (getLandingGateSnapshot() === 'idle') {
      startLandingCheck();
    }
    (async () => {
      try {
        const target = await resolveDefaultLandingUrlAfterAuth();
        if (!cancelled && target) {
          appHistory.replace(target);
        }
      } finally {
        if (!cancelled) {
          finishLandingCheck();
          inFlightRef.current = false;
          completedForUserRef.current = uid;
          markDefaultLandingConsumed(uid);
        }
      }
    })();
    return () => {
      cancelled = true;
      inFlightRef.current = false;
      if (getLandingGateSnapshot() === 'checking') {
        finishLandingCheck();
      }
    };
  }, [login.isAuthenticated, (_login$user2 = login.user) === null || _login$user2 === void 0 ? void 0 : _login$user2.user_id, pathname]);
  return null;
};
export default PostAuthDefaultLanding;